/**
 * 아티스트 관련 Composable
 * 새로운 공통 패턴을 사용한 V2 API 호출과 상태 관리
 */

import { computed } from 'vue'
import { usePagination } from './base/usePagination.js'

/**
 * @typedef {Object} ArtistSearchParams
 * @property {string} [q=''] - 검색어
 * @property {boolean} [like=false] - 관심 아티스트만 조회
 * @property {number} [page=0] - 페이지 번호 (0부터 시작)
 * @property {number} [size=24] - 페이지 크기
 * @property {string} [sort='createdAt'] - 정렬 필드
 * @property {string} [direction='desc'] - 정렬 방향 ('asc' | 'desc')
 */

/**
 * 아티스트 데이터 관리를 위한 Composable
 * @returns {Object} 아티스트 관련 상태와 메서드들
 */
export function useArtists() {
  // 기본 페이징 기능 사용
  const {
    items,
    loading,
    error,
    currentPageData,
    fetchItems,
    search,
    loadMore,
    goToPage,
    clearCache,
    reset
  } = usePagination({
    domain: 'artists',
    endpoint: '/v2/artists',
    defaultParams: {
      q: '',
      like: false
    },
    defaultSize: 24,
    defaultSort: 'createdAt',
    defaultDirection: 'desc'
  })

  // 도메인별 별칭 제공 (하위 호환성)
  const artists = computed(() => items.value)

  /**
   * 아티스트 목록 조회 (기존 API 호환)
   * @param {ArtistSearchParams} params - 검색 파라미터
   * @param {boolean} [append=false] - 기존 목록에 추가할지 여부
   * @returns {Promise<Object>} 페이징된 아티스트 목록
   */
  const fetchArtists = async (params = {}, append = false) => {
    return fetchItems(params, append)
  }

  /**
   * 아티스트 검색
   * @param {ArtistSearchParams} searchParams - 검색 파라미터
   * @returns {Promise<Object>} 검색 결과
   */
  const searchArtists = async (searchParams = {}) => {
    return search(searchParams)
  }

  /**
   * 다음 페이지 아티스트 로드 (무한스크롤)
   * @param {ArtistSearchParams} baseParams - 기본 검색 파라미터
   * @returns {Promise<Object>} 다음 페이지 데이터
   */
  const loadMoreArtists = async (baseParams = {}) => {
    return loadMore(baseParams)
  }

  return {
    // 읽기 전용 상태 (하위 호환성을 위한 별칭 포함)
    artists,
    items,
    loading,
    error,
    currentPageData,
    
    // 액션 (하위 호환성을 위한 별칭 포함)
    fetchArtists,
    searchArtists,
    loadMoreArtists,
    goToPage,
    clearCache,
    reset
  }
}